import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { ACUITY } from "@/data/site";

let embedScriptLoaded = false;

/**
 * Embeds the official Acuity (Squarespace) scheduler inside the Golden Touch page.
 * embed.js auto-resizes the iframe to its content so users don't scroll a tiny box.
 */
export default function AcuityEmbed({ category, appointmentType }) {
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);
  let src = ACUITY.embedSrc;
  if (appointmentType) src += `&appointmentType=${encodeURIComponent(appointmentType)}`;
  else if (category) src += `&appointmentType=category:${encodeURIComponent(category)}`;

  useEffect(() => {
    // Load Acuity's resize helper once.
    if (!embedScriptLoaded) {
      const s = document.createElement("script");
      s.src = "https://embed.acuityscheduling.com/js/embed.js";
      s.async = true;
      document.body.appendChild(s);
      embedScriptLoaded = true;
    }
  }, []);

  return (
    <div className="w-full">
      <div className="relative border border-gold/40 bg-onyx p-1.5 shadow-[0_0_0_1px_rgba(212,175,55,0.15)]">
        <div className="flex items-center justify-between border-b border-gold/25 bg-charcoal px-4 py-3">
          <span className="font-display text-lg text-ivory tracking-wide">
            Book Your <span className="text-gold italic">Appointment</span>
          </span>
          <a
            href={ACUITY.directUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="acuity-open-new-tab"
            className="hidden sm:inline-flex items-center gap-1.5 text-[0.68rem] uppercase tracking-[0.18em] text-gold/80 hover:text-gold transition-colors"
          >
            New tab <ExternalLink size={12} />
          </a>
        </div>
        <div className="relative bg-ivory overflow-hidden">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-ivory">
              <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
              <span className="sr-only">Loading booking calendar</span>
            </div>
          )}
          <iframe
            ref={iframeRef}
            title="Book an appointment with Golden Touch Lash and Brow"
            src={src}
            width="100%"
            height="1000"
            frameBorder="0"
            onLoad={() => setLoaded(true)}
            className="w-full min-h-[900px] block"
          />
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-muted-ivory">
        Trouble viewing the calendar?{" "}
        <a
          href={ACUITY.directUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-gold hover:underline"
        >
          Open the scheduler in a new tab <ExternalLink size={13} />
        </a>
      </p>
    </div>
  );
}
